// Default landing for the Directory feature. Big centered search bar
// with live autocomplete that navigates to the property detail page
// on select. Below the search, a small "top warm opportunities" preview
// (placeholder text in Phase A — wired up to real data in Phase C).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import DirectoryTopNav from '../components/DirectoryTopNav';
import ApolloBadge from '../components/ApolloBadge';

const SEARCH_DEBOUNCE_MS = 120;

export default function DirectorySearchPage() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const lastRequestRef = useRef(0);

  // Initial metadata fetch — lets us show counts in the hero copy.
  useEffect(() => {
    let cancelled = false;
    api.getDirectoryMeta()
      .then(d => { if (!cancelled) setMeta(d); })
      .catch(err => { if (!cancelled) setMetaErr(err?.responseBody?.error || err.message || 'Failed to load directory'); });
    return () => { cancelled = true; };
  }, []);

  // Debounced live search.
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setActiveIdx(-1);
      return;
    }
    const reqId = ++lastRequestRef.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.searchDirectory(query);
        if (reqId !== lastRequestRef.current) return; // stale
        setResults(data.results || []);
        setActiveIdx(-1);
      } catch {
        if (reqId === lastRequestRef.current) setResults([]);
      } finally {
        if (reqId === lastRequestRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  function onSelect(propertyId) {
    navigate(`/sales/directory/property/${encodeURIComponent(propertyId)}`);
  }

  function onKeyDown(e) {
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[activeIdx >= 0 ? activeIdx : 0];
      if (pick) onSelect(pick.id);
    } else if (e.key === 'Escape') {
      setQuery('');
      setResults([]);
    }
  }

  const counts = useMemo(() => {
    if (!meta) return null;
    const t = meta.metadata?.totals || {};
    const p2 = meta.metadata?.phase2 || {};
    return {
      properties: t.properties || 0,
      apolloProperties: p2.aln_properties_with_apollo || 0,
      opportunities: p2.aln_opportunity_count || 0,
      coverage: p2.overall_aln_coverage_pct || 0,
    };
  }, [meta]);

  return (
    <div className="directory-page">
      <DirectoryTopNav />

      <div className="directory-search-hero">
        <h1 className="directory-search-hero__title">DFW Property Directory</h1>
        {counts ? (
          <p className="directory-search-hero__subtitle">
            <strong>{counts.properties.toLocaleString()}</strong> apartment properties &middot;{' '}
            <strong>{counts.apolloProperties.toLocaleString()}</strong> Apollo customers &middot;{' '}
            <strong>{counts.opportunities.toLocaleString()}</strong> opportunities &middot;{' '}
            <strong>{counts.coverage}%</strong> coverage
          </p>
        ) : metaErr ? (
          <p className="directory-search-hero__subtitle" style={{ color: '#dc2626' }}>{metaErr}</p>
        ) : (
          <p className="directory-search-hero__subtitle">Loading...</p>
        )}

        <div className="directory-search-box">
          <input
            ref={inputRef}
            type="text"
            className="directory-search-input"
            placeholder="Search any property name..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
            autoComplete="off"
            aria-label="Search property names"
          />
          {loading ? <span className="directory-search-spinner" aria-hidden="true">...</span> : null}

          {results.length > 0 ? (
            <ul className="directory-autocomplete" role="listbox">
              {results.map((r, i) => (
                <li
                  key={r.id}
                  role="option"
                  aria-selected={i === activeIdx}
                  className={'directory-autocomplete__item' + (i === activeIdx ? ' directory-autocomplete__item--active' : '')}
                  onMouseDown={(e) => { e.preventDefault(); onSelect(r.id); }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className="directory-autocomplete__check" aria-hidden="true">
                    {r.apollo_customer ? <span className="directory-autocomplete__check-icon">&#10003;</span> : null}
                  </span>
                  <span className="directory-autocomplete__name">{r.name}</span>
                  <span className="directory-autocomplete__city">{r.city}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {query.trim().length >= 2 && !loading && results.length === 0 ? (
            <div className="directory-autocomplete directory-autocomplete--empty">No properties match &ldquo;{query}&rdquo;</div>
          ) : null}
        </div>

        <p className="directory-search-hint">
          Search by property name. To browse by management company or see warm leads, use the tabs above.
        </p>
      </div>

      {/* Empty-state preview slot — Phase C populates this with top warm
          opportunities so reps have something to act on without typing. */}
      <div className="directory-empty-preview">
        <ApolloBadge isCustomer={true} />
        <span style={{ marginLeft: 12, color: '#475569' }}>
          Green check = Apollo customer. Properties under regionals where Apollo already serves are warm pitches.
        </span>
      </div>
    </div>
  );
}
